/* eslint-disable consistent-return */
import {
	httpGet,
	httpPut,
	HTTPDestinationType,
} from '../lib/adapters/httpAdapter'
import {
	assertApiErrorOrThrow,
	extractApiErrorDetails,
} from '../lib/errors/apiError'
import { getEnvVariable } from './getenvService'
import { UserPermissionsResponse } from '../../lib/constants/permissions'
import {
	CompanyDetailsApiResponse,
	CompanyDetailsByIdsResponse,
} from './models/companyDetails'
import { BrandCdEnum } from './models/expApiTypes'

export async function getCompanyGoals(companyId: string, token?: string) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/companies/${companyId}/goals`,
			{ token }
		)
		return response as { goals: Array<{ yearNo: number; gciGoalAm: number }> }
	} catch (error) {
		assertApiErrorOrThrow(error, 'Error fetching company goals')
	}
}

export async function putCompanyGoals(
	companyId: string,
	goal: {
		yearNo: number
		gciGoalAm: number
	},
	token?: string
) {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const response = await httpPut(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/companies/${companyId}/goals`,
			{
				token,
				body: {
					goal,
				},
			}
		)
		return response as { success: boolean }
	} catch (error) {
		assertApiErrorOrThrow(error, 'Error updating company goals')
	}
}

/**
 * Fetches detailed company information for multiple company IDs.
 *
 * Calls the /api/companies/{companyId} endpoint for each company ID to retrieve
 * comprehensive company details including name, reference ID, and brand information.
 * Also extracts and returns a list of unique brand codes from all companies.
 *
 * @param companyIds - Array of company IDs (partyId from ExpAPI) to fetch details for
 * @param token - Optional authentication token for the API request
 * @returns Promise that resolves to an object containing company details array and unique brand codes
 *
 * @throws {Error} Throws user-friendly error messages on API failure
 */
export async function getCompanyDetailsByIds(
	companyIds: string[],
	token?: string
): Promise<CompanyDetailsByIdsResponse> {
	try {
		if (!companyIds || companyIds.length === 0) {
			throw new Error('No company ID provided')
		}

		// Validate each company ID
		if (companyIds.some(id => !id || id.trim() === '')) {
			throw new Error('No company ID provided')
		}

		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')

		const detailPromises = companyIds.map(async companyId => {
			const response = (await httpGet(
				HTTPDestinationType.EXTERNAL,
				`${apiBaseUrl}/api/companies/${companyId}`,
				{ token }
			)) as CompanyDetailsApiResponse

			return response
		})

		const companies = await Promise.all(detailPromises)

		// Extract unique brand codes from all companies
		const brandCodeSet = new Set<BrandCdEnum>()
		for (const company of companies) {
			if (
				company.brandCd &&
				Object.values(BrandCdEnum).includes(company.brandCd as BrandCdEnum)
			) {
				brandCodeSet.add(company.brandCd as BrandCdEnum)
			}
		}
		return {
			companies,
			brandCodes: Array.from(brandCodeSet),
		}
	} catch (error: unknown) {
		assertApiErrorOrThrow(
			error,
			`Error fetching company details for ID ${companyIds}`
		)
	}
}

/**
 * Retrieves the list of companies a user has permissions for, based on their party type code.
 *
 * @param userId - The user's Okta ID to fetch permissions for.
 * @param token - The user's access token.
 * @param useDashSecurity - When true (LD flag TEMP_BWC_28_ENABLE_DASH_SECURITY), calls the
 * DASH security endpoint (`dashpermissions`) instead of the legacy Permit endpoint (`permissions`).
 * @returns A promise that resolves to a `UserPermissionsResponse` object or an empty array if no permissions are found.
 * @throws Will log and rethrow an error if the API request fails.
 */
export async function getUserMultiCompanies(
	userId: string,
	token?: string,
	useDashSecurity?: boolean,
): Promise<UserPermissionsResponse | []> {
	try {
		const apiBaseUrl = await getEnvVariable('NEXT_PUBLIC_EXP_API_BASE_URL')
		const permissionsPath = useDashSecurity ? 'dashpermissions' : 'permissions'

		const response = (await httpGet(
			HTTPDestinationType.EXTERNAL,
			`${apiBaseUrl}/api/users/${userId}/${permissionsPath}`,
			{ token },
		)) as UserPermissionsResponse

		return response
	} catch (error: unknown) {
		console.error('Error fetching companies by permissions', error)
		assertApiErrorOrThrow(error, 'Error fetching companies by permissions')
	}
}
